import * as React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import SidePanel from 'src/components/sidePanel/SidePanel';
import { badgeCardTypeOptions } from 'src/features/cards/utils/constants';
import { useGetEvents } from 'src/features/events/api/events';
import { EventDetails } from 'src/features/events/components/EventDetails';
import { badgeStatusFilterOptions, badgeStatusOptions, eventMessages } from 'src/features/events/utils';
import { CustomDataTableControls } from 'src/features/statistics/components/CustomDataTableControls';
import useFilters from 'src/hooks/useFilters';
import usePagination from 'src/hooks/usePagination';
import useSearch from 'src/hooks/useSearch';
import useSorting from 'src/hooks/useSorting';
import { useParking } from 'src/providers/ParkingProvider';
import { CellBadge } from 'src/registry/components/table/cells/CellBadge';
import { CellDatetime } from 'src/registry/components/table/cells/CellDatetime';
import { CellIndex } from 'src/registry/components/table/cells/CellIndex';
import { DataTable } from 'src/registry/components/table/DataTable';
import DataTableColumnGenericHeaderDefinition from 'src/registry/components/table/DataTableColumnGenericHeaderDefinition';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import DataTableDateDropdown from 'src/registry/components/table/DataTableDateDropdown';
import { DataTablePagination } from 'src/registry/components/table/DataTablePagination';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useRowSelection from 'src/registry/hooks/useRowSelection';
import { Badge } from 'src/registry/ui/Badge';
import { generalMessages } from 'src/utils/messages';

import type { Event } from 'src/features/events/types/event';
import { CellLongText } from 'src/features/memberships/components/table/cells/CellLongText';

interface EventTableProps {
    isDashboard?: boolean;
}
const FULL_LOG_PANEL_NAME = 'full-log-side-panel';

const EventTable = ({ isDashboard = false }: EventTableProps) => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const { clearRowSelection, ...rowSelection } = useRowSelection({ sidePanelName: FULL_LOG_PANEL_NAME });
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'card_number' });
    const { queryFiltersOptions, ...filters } = useFilters();
    const { queryPaginationOptions, ...pagination } = usePagination({
        pageSize: 100,
    });
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'timestamp', desc: true }],
    });

    const [dateTime, setDateTime] = React.useState(() => {
        const currentDate = new Date();
        const today = new Date(currentDate.getTime());
        today.setHours(23);
        today.setMinutes(59);
        today.setSeconds(59);
        const lastWeek = new Date(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7).getTime());
        return { afterDate: lastWeek, beforeDate: today };
    });

    const { data, isLoading, isError, errorUpdateCount } = useGetEvents({
        parkingId: parkingId,
        date_greater_than: dateTime.afterDate.toISOString(),
        date_less_than: dateTime.beforeDate.toISOString(),
        ...queryPaginationOptions,
        ...(isDashboard ? {} : querySortingOptions),
        ...(isDashboard ? {} : queryFiltersOptions),
        ...(isDashboard ? {} : querySearchOptions),
        config: {
            // Let's refetch every 5 seconds
            refetchInterval: (query) => {
                if (!isDashboard) return false;
                if (!query.state.error) return 5 * 1000;
                else return 60 * 1000;
            },
            refetchIntervalInBackground: false,
            enabled: !!parkingId,
            refetchOnWindowFocus: true,
        },
    });

    const DataTableControls = React.useMemo(
        () =>
            CustomDataTableControls(
                <DataTableDateDropdown
                    setValue={setDateTime}
                    inline
                    afterDate={dateTime.afterDate}
                    beforeDate={dateTime.beforeDate}
                />,
            ),
        [setDateTime, dateTime, parkingId, querySortingOptions, queryFiltersOptions],
    );

    const getRowData = React.useMemo(() => {
        const firstSelected = Object.keys(rowSelection.rowSelectionState)[0];
        if (!data || !firstSelected) return;
        return data.results.find((cards) => cards.id === parseInt(firstSelected));
    }, [data, rowSelection.rowSelectionState]);

    const columnHelper = createColumnHelper<Event>();

    const columns = [
        columnHelper.accessor('id', {
            header: DataTableColumnGenericHeaderDefinition,
            cell: CellIndex,
            maxSize: 45,
            meta: { title: '#' },
        }),
        columnHelper.accessor('timestamp', {
            header: DataTableColumnHeader,
            enableSorting: !isDashboard,
            cell: CellDatetime,
            maxSize: 160,
            meta: {
                title: formatMessage(generalMessages.timestamp),
            },
        }),
        columnHelper.accessor('event_type', {
            header: formatMessage(eventMessages.event),
            cell: (props) => <CellBadge badgeOptions={badgeStatusOptions} {...props} />,
            enableHiding: !isDashboard,
            enableSorting: !isDashboard,
            enableColumnFilter: !isDashboard,
            maxSize: 200,
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                badgeOptions: badgeStatusFilterOptions,
                filterLabel: formatMessage(eventMessages.category),
                customDropdownMenuLabel: formatMessage(eventMessages.filterByCategoryEvent),
            },
        }),
        columnHelper.accessor('card_number', {
            header: formatMessage(eventMessages.customer),
            enableHiding: !isDashboard,
            maxSize: 200,
            cell: ({ getValue, row }) => {
                return (
                    <div className="flex flex-col">
                        <span>{getValue()}</span>
                        <div className="flex w-full flex-wrap gap-x-2">
                            {row.original.tags?.map((tag) => (
                                <Badge key={tag.id} theme={tag.card_tag.theme} className="mt-1">
                                    {tag.card_tag.text}
                                </Badge>
                            ))}
                        </div>
                    </div>
                );
            },
        }),
        columnHelper.accessor('card_type', {
            header: DataTableColumnHeader,
            cell: (props) => <CellBadge badgeOptions={badgeCardTypeOptions} {...props} />,
            enableHiding: !isDashboard,
            enableColumnFilter: !isDashboard,
            maxSize: 160,
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                title: formatMessage(eventMessages.cardType),
                badgeOptions: badgeCardTypeOptions,
            },
        }),
        columnHelper.accessor('point_name', {
            enableHiding: !isDashboard,
            header: formatMessage(eventMessages.pointName),
            maxSize: 160,
        }),
        columnHelper.accessor('message', {
            header: formatMessage(generalMessages.message),
            cell: CellLongText,
            meta: {
            isLongText: true,
            },
        }),
    ];

    if (isDashboard) {
        // remove the first column
        columns.shift();
    }

    return (
        <>
            <DataTable
                columns={columns}
                data={data}
                semantics={{
                    title: formatMessage(eventMessages.log),
                    entryName: formatMessage(eventMessages.event),
                    entryNames: formatMessage(eventMessages.events),
                    sidePanelName: FULL_LOG_PANEL_NAME,
                }}
                {...(isDashboard ? {} : { dataTableBodyProps: { cellVerticalPadding: 'snug' } })}
                {...(isDashboard ? {} : { sorting })}
                {...(isDashboard ? {} : { filters })}
                {...(isDashboard ? {} : { search })}
                pagination={pagination}
                isError={isError}
                isLoading={isLoading}
                rowSelection={rowSelection}
                getRowId={(row) => row.id.toString()}
                errorUpdateCount={errorUpdateCount}
                components={
                    isDashboard
                        ? {}
                        : {
                              controls: DataTableControls,
                              pagination: DataTablePagination,
                          }
                }
            />
            <SidePanel onClose={clearRowSelection} name={FULL_LOG_PANEL_NAME}>
                <EventDetails event={getRowData} />
            </SidePanel>
        </>
    );
};

export default EventTable;
